import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ErrorFieldComponent } from '../error-field/error-field.component';
import { IndirizzoAutocompleteService } from './http/indirizzo-autocomplete.service';
import { IndirizzoDto } from './models/indirizzo-dto.model';
import { IndirizzoAutocompleteStore } from './store/indirizzo-autocomplete.store';

const MIN_QUERY_LENGTH = 3;

/**
 * @example
 *  <siat-indirizzo-autocomplete-field
 *    [formGroup]="formModificaStruttura"
 *    controlName="indirizzo"
 *    id="indirizzo"
 *    label="Indirizzo"
 *  ></siat-indirizzo-autocomplete-field>
 */
@UntilDestroy()
@Component({
  selector: 'siat-indirizzo-autocomplete-field',
  templateUrl: './indirizzo-autocomplete-field.component.html',
  styleUrls: ['./indirizzo-autocomplete-field.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ErrorFieldComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IndirizzoAutocompleteFieldComponent),
      multi: true,
    },
    IndirizzoAutocompleteService,
    IndirizzoAutocompleteStore,
  ],
})
export class IndirizzoAutocompleteFieldComponent
  implements OnInit, ControlValueAccessor
{
  /**
   * The form group that contains the control.
   */
  formGroup = input<FormGroup>();

  /**
   * The name of the control.
   */
  controlName = input<string>('');

  /**
   * The id of the control.
   */
  id = input<string>('');

  /**
   * The placeholder of the control.
   */
  placeholder = input<string>('');

  /**
   * The label of the control.
   */
  label = input<string>('');

  /**
   * The name of the control.
   */
  name = input<string>('');

  /**
   * The appearance of the control.
   */
  appearance = input<MatFormFieldAppearance>('outline');

  /**
   * The store for the autocomplete.
   */
  protected store = inject(IndirizzoAutocompleteStore);

  /**
   * The internal control.
   */
  private internalControl = new FormControl<IndirizzoDto | string | null>(null);

  /**
   * The options for the autocomplete.
   */
  public options = this.store.indirizzi;

  /**
   * The loading state for the autocomplete.
   */
  public isLoading = this.store.isLoading;

  /**
   * @returns {FormControl<IndirizzoDto | string | null>}
   * The control.
   */
  get control(): FormControl<IndirizzoDto | string | null> {
    if (this.formGroup() && this.controlName()) {
      return this.formGroup()?.controls[this.controlName()] as FormControl<
        IndirizzoDto | string | null
      >;
    }
    return this.internalControl;
  }

  /**
   * @returns {boolean}
   * True if the control is disabled.
   */
  get disabled(): boolean {
    return this.control.disabled;
  }

  /**
   * @returns {boolean}
   * True se il valore selezionato e' un `IndirizzoDto` completo di `cap` —
   * senza cap il servizio non ha davvero geocodificato l'indirizzo (via
   * generica/ambigua), quindi non lo trattiamo come verificato anche se
   * l'utente ha cliccato un suggerimento.
   */
  get isVerified(): boolean {
    const value = this.control.value;
    return !!value && typeof value === 'object' && !!value.cap;
  }

  /**
   * @returns {boolean}
   * True if the control has no results.
   */
  get showNoResults(): boolean {
    const value = this.control.value;
    const query = typeof value === 'string' ? value : '';
    return (
      !this.isLoading() &&
      query.length >= MIN_QUERY_LENGTH &&
      this.options().length === 0
    );
  }

  constructor() {
    this.internalControl.valueChanges.subscribe(value => {
      this.onChange(value as IndirizzoDto);
      this.onTouched();
    });
  }

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.control.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), untilDestroyed(this))
      .subscribe(value => {
        const query = typeof value === 'string' ? value : '';
        if (query.length >= MIN_QUERY_LENGTH) {
          this.store.searchIndirizzi({ query });
        } else {
          this.store.clearIndirizzi();
        }
      });
  }

  /**
   * Autocomplete onBlur event.
   */
  onBlur(): void {
    if (!this.control.dirty || typeof this.control.value !== 'string') return;
    this.control.setValue(null);
    this.control.markAsTouched();
  }

  /**
   * @inheritdoc
   */
  displayFn(item: IndirizzoDto | string): string {
    return typeof item === 'string' ? item : (item?.via ?? '');
  }

  /**
   * @inheritdoc
   */
  onChange: (value: IndirizzoDto | null) => void = () => {};

  /**
   * @inheritdoc
   */
  onTouched: () => void = () => {};

  /**
   * @inheritdoc
   */
  registerOnChange(changeCallback: (value: IndirizzoDto | null) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * @inheritdoc
   */
  registerOnTouched(touchedCallback: () => void): void {
    this.onTouched = touchedCallback;
  }

  /**
   * @inheritdoc
   */
  writeValue(value: IndirizzoDto | null): void {
    if (!this.formGroup() || !this.controlName()) {
      this.internalControl.setValue(value, { emitEvent: false });
    } else {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  /**
   * Set disabled state.
   * @param isDisabled
   */
  setDisabledState(isDisabled: boolean): void {
    if (!this.formGroup() || !this.controlName()) {
      if (isDisabled) {
        this.internalControl.disable();
      } else {
        this.internalControl.enable();
      }
    } else {
      if (isDisabled) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    }
  }
}
